import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Stack,
  Grid,
} from "@mui/material";
import { useSnackbarErrorHandler } from "utils/errorHandling";
import { ActionType, useContactMetierActions } from "providers";
import SaveIcon from "@mui/icons-material/Save";
import { enqueueSnackbar } from "notistack";
import { ToastMessages } from "enums";
import { compare } from "fast-json-patch";
import { MetierBox } from "./MetierBox";
import type { ContactMetier, Poste } from "models";
import { contactsMetiersService } from "services";
import { usePostes } from "hooks";
import { PosteAutoComplete } from "components/inputs";
import { contactsMetiersUtil } from "utils";
import { PerimetreLink } from "./PerimetreLink";
import { TerritoireLink } from "./TerritoireLink";
import { CommuneLink } from "./CommuneLink";

export function EditContactMetierDialog(): ReactNode {
  const { actionType, closeActionDialog, targetContactMetier, isActionDialogOpen, onEdit } =
    useContactMetierActions();
  const { postes, isPostesLoading } = usePostes();
  const { catchErrors, isLoading } = useSnackbarErrorHandler();
  const [nouveauPoste, setNouveauPoste] = useState<Poste | null>(null);

  useEffect(() => {
    setNouveauPoste(targetContactMetier?.poste ?? null);
  }, [targetContactMetier]);

  if (targetContactMetier == null) {
    return null;
  }

  function confirm(): void {
    async function confirmAndClose(): Promise<void> {
      if (targetContactMetier == null || nouveauPoste == null) {
        return;
      }

      const newContactMetier: ContactMetier = { ...targetContactMetier, poste: nouveauPoste };

      await contactsMetiersService.patch(
        targetContactMetier.id,
        compare(targetContactMetier, newContactMetier)
      );

      closeActionDialog();
      enqueueSnackbar({
        variant: "success",
        autoHideDuration: 2000,
        preventDuplicate: false,
        message: ToastMessages.CONTACT_METIER_EDIT_SUCCESS,
      });
      await onEdit?.(newContactMetier);
    }
    void catchErrors(confirmAndClose);
  }

  return (
    <Dialog
      open={isActionDialogOpen && actionType === ActionType.EDIT}
      onClose={closeActionDialog}
      fullWidth>
      <DialogTitle>Éditer le poste d'un contact métier</DialogTitle>
      <DialogContent>
        <Grid container direction="column" spacing={1}>
          {contactsMetiersUtil.isCommune(targetContactMetier) && (
            <Stack direction="row" gap={2}>
              <Typography>Commune&nbsp;: </Typography>
              <CommuneLink commune={targetContactMetier?.commune} />
            </Stack>
          )}
          {contactsMetiersUtil.isPerimetre(targetContactMetier) && (
            <Stack direction="row" gap={2}>
              <Typography>Périmètre&nbsp;: </Typography>
              <PerimetreLink perimetre={targetContactMetier?.perimetre} />
            </Stack>
          )}
          {contactsMetiersUtil.isTerritoire(targetContactMetier) && (
            <Stack direction="row" gap={2}>
              <Typography>Territoire&nbsp;: </Typography>
              <TerritoireLink territoire={targetContactMetier?.territoire} />
            </Stack>
          )}
          <Stack direction="row" gap={2}>
            <Typography>Métier&nbsp;: </Typography>
            <MetierBox metier={targetContactMetier.metier} />
          </Stack>
          <PosteAutoComplete
            postes={postes}
            label="Poste *"
            onChange={setNouveauPoste}
            value={nouveauPoste}
            isLoading={isPostesLoading}
          />
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={closeActionDialog} color="error">
          Annuler
        </Button>
        <Button
          onClick={confirm}
          variant="contained"
          autoFocus
          loading={isLoading}
          loadingPosition="start"
          // TODO utiliser react-hook-form pour forcer cette vérification
          // et afficher un message au user
          disabled={nouveauPoste == null}
          startIcon={<SaveIcon />}>
          Enregistrer
        </Button>
      </DialogActions>
    </Dialog>
  );
}
